import React, { useImperativeHandle, useRef, useMemo, forwardRef } from "react";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { getFieldMap } from "../../../../utils/inputs";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import { getWeddingDate } from "../../../../utils/formatDate";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import AddIcon from "@mui/icons-material/Add";
import InvitationGallery from "../../../InvitationGallery";
import PartnersSocialList from "../Components/PartnersSocialList";
import CountDown from "../CountDown";
import MapButton from "../Components/MapButton";
import MapComponent from "../Components/MapComponent/MapComponent";
import Comments from "../Components/Comments";
import Footer from "../Components/Footer";
import { TemplateWrapper } from "./styles";

const BotanicalCorner = ({ position }: { position: string }) => (
	<svg className={`botanical-corner botanical-${position}`} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
		<g opacity="0.55">
			<path d="M10,10 Q40,5 35,40 Q30,25 10,10Z" fill="currentColor" />
			<path d="M10,10 Q5,40 40,35 Q25,30 10,10Z" fill="currentColor" />
			<path d="M10,10 Q50,15 45,55 Q35,35 10,10Z" fill="currentColor" opacity="0.6" />
			<path d="M10,10 Q15,50 55,45 Q35,35 10,10Z" fill="currentColor" opacity="0.6" />
			<circle cx="10" cy="10" r="2" fill="currentColor" />
			<circle cx="30" cy="18" r="1.5" fill="currentColor" opacity="0.7" />
			<circle cx="18" cy="30" r="1.5" fill="currentColor" opacity="0.7" />
			<path d="M10,10 L60,8" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.4" />
			<path d="M10,10 L8,60" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.4" />
		</g>
	</svg>
);

const GoldDivider = () => (
	<svg className="gold-divider" viewBox="0 0 320 24" xmlns="http://www.w3.org/2000/svg">
		<line x1="0" y1="12" x2="130" y2="12" stroke="currentColor" strokeWidth="0.8" opacity="0.7" />
		<path d="M140,12 Q148,4 156,12 Q164,20 172,12 Q180,4 188,12" stroke="currentColor" strokeWidth="1" fill="none" />
		<circle cx="160" cy="12" r="3" fill="currentColor" />
		<circle cx="140" cy="12" r="1.5" fill="currentColor" opacity="0.7" />
		<circle cx="180" cy="12" r="1.5" fill="currentColor" opacity="0.7" />
		<line x1="190" y1="12" x2="320" y2="12" stroke="currentColor" strokeWidth="0.8" opacity="0.7" />
	</svg>
);

const OrnamentalFrame = ({ hasImages }: { hasImages: boolean }) => (
	<svg className="ornamental-frame" viewBox={`0 0 380 ${hasImages ? "760" : "520"}`} xmlns="http://www.w3.org/2000/svg">
		<g transform={hasImages ? "scale(1, 1.46)" : ""}>
			<rect
				x="8"
				y="8"
				width="364"
				height="504"
				rx="2"
				ry="2"
				fill="none"
				stroke="currentColor"
				strokeWidth="1"
				opacity="0.5"
			/>
			<rect
				x="16"
				y="16"
				width="348"
				height="488"
				rx="1"
				ry="1"
				fill="none"
				stroke="currentColor"
				strokeWidth="0.5"
				opacity="0.3"
			/>
			<path d="M8,60 Q30,55 35,35 Q55,30 60,8" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
			<path d="M320,8 Q325,30 345,35 Q350,55 372,60" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
			<path d="M8,460 Q30,465 35,485 Q55,490 60,512" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
			<path
				d="M320,512 Q325,490 345,485 Q350,465 372,460"
				fill="none"
				stroke="currentColor"
				strokeWidth="1"
				opacity="0.5"
			/>
			<circle cx="8" cy="8" r="3" fill="currentColor" opacity="0.5" />
			<circle cx="372" cy="8" r="3" fill="currentColor" opacity="0.5" />
			<circle cx="8" cy="512" r="3" fill="currentColor" opacity="0.5" />
			<circle cx="372" cy="512" r="3" fill="currentColor" opacity="0.5" />
		</g>
	</svg>
);

interface Props {
	data: Invitations;
	gallery: IGallery[];
	colorMatrix: TemplateColorObject;
	canEdit?: boolean;
}

const Template = forwardRef(({ data, gallery, colorMatrix: colorMatrixProp, canEdit = false }: Props, ref) => {
	const commentsSectionRef = useRef<HTMLElement>(null);
	const locationSectionRef = useRef<HTMLElement>(null);

	useImperativeHandle(ref, () => ({
		scrollToComments: () =>
			commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" }),
		scrollToLocation: () =>
			locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
	}));

	const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
	const invitationId = data?._id || "";
	const lang = data?.language === LANG_EN ? langEN : langAR;
	const fieldMap = getFieldMap(invitationId);

	const invitation_type_title = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.the_engagement_of;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.the_katb_ktab_of;
			default:
				return lang.the_wedding_of;
		}
	}, [data]);

	const invitation_getting_text = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.GETTING_ENGAGED;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.GETTING_KATB_KTAB;
			default:
				return lang.GETTING_MARRIED;
		}
	}, [data]);

	const invitation_location_header = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.engagement_location;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.katb_ktab_location;
			default:
				return lang.wedding_location;
		}
	}, [data]);

	return (
		<TemplateWrapper colormatrix={colorMatrixProp} className="invitation-box" id="template_wrapper">
			<div className="watercolor-bg" />
			<div className="texture-overlay" />

			{/* Section 01 — Hero */}
			<div className="section_01">
				<div className="hero-paper">
					<OrnamentalFrame hasImages={data?.has_images || false} />
					<BotanicalCorner position="tl" />
					<BotanicalCorner position="tr" />
					<BotanicalCorner position="bl" />
					<BotanicalCorner position="br" />

					<div className="hero-inner">
						<p className="wedding-of">{invitation_type_title}</p>
						<GoldDivider />

						<img
							src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ycAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAAAALAAAAAABAAEAAAICTAEAOw=="
							alt=""
							style={{ display: "none" }}
							id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
						/>
						{data?.has_images && data?.main_image && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<div className="main-image-wrap">
								<img
									id="main_image"
									src={data?.main_image}
									alt="couple"
									data-dispatch-id={fieldMap.MAIN_IMAGE}
									className={`main-image ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
								/>
								<div className="image-gold-border" />
							</div>
						)}

						<div className="names-block">
							<h1 className="groom-name guide_edit_field" data-dispatch-id={fieldMap.GROOM_NAME}>
								{data?.groom_name || "Groom"}
							</h1>
							<span className="ampersand">&</span>
							<h1 className="bride-name" data-dispatch-id={fieldMap.BRIDE_NAME}>
								{data?.bride_name || "Bride"}
							</h1>
						</div>

						<GoldDivider />
						<p
							className="date-text"
							data-dispatch-id={fieldMap.WEDDING_DATE}
							style={{ fontSize: data.language === LANG_AR ? "25px" : "" }}
						>
							{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
						</p>
						{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
							<p
								className={`save-date-text animate-on-scroll delay-1 ${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.SAVE_THE_DATE}
							>
								{data?.save_the_date_text}
							</p>
						)}

						<div className="save-btn-wrap">
							<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
						</div>
					</div>
				</div>
			</div>

			{/* Section 02 — Countdown */}
			<div className="section_02">
				<div className="countdown-paper">
					<div className="countdown-label">{invitation_getting_text}</div>
					<GoldDivider />
					<CountDown
						data={data || ({} as Invitations)}
						daysString={lang.days}
						hoursString={lang.hours}
						minutesString={lang.mins}
						secondsString={lang.sec}
					/>
				</div>
			</div>

			{/* Section 03 — Partners */}
			<div className="section_03">
				<div className="section-inner">
					{data?.is_islamic && (
						<p className="dua-text">بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْرٍ</p>
					)}
					<GoldDivider />
					{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
						<p
							className={`main-intro animate-on-scroll delay-1 ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""}`}
							data-dispatch-id={fieldMap.INTRO_TEXT}
						>
							{data?.intro_text}
						</p>
					)}
					{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
						<p
							className={`sub-intro animate-on-scroll delay-2 ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""}`}
							data-dispatch-id={fieldMap.SUB_INTRO}
						>
							{data?.sub_intro_text}
						</p>
					)}

					<div className="partners-grid">
						<div className="partner-card">
							{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
								<div className="partner-img-frame">
									<img
										src={data?.groom_image}
										alt="groom"
										data-dispatch-id={fieldMap.GROOM_IMAGE}
										className={`partner-img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
									/>
								</div>
							)}
							<p className="partner-name animate-on-scroll delay-1" data-dispatch-id={fieldMap.GROOM_NAME}>
								{data?.groom_name || "Groom"}
							</p>
							<PartnersSocialList
								socials={data?.partner_socials_1 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_1}
							/>
						</div>

						<div className="partner-divider-vert">
							<div className="vert-line" />
							<span className="heart-icon">♥</span>
							<div className="vert-line" />
						</div>

						<div className="partner-card">
							{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
								<div className="partner-img-frame">
									<img
										src={data?.bride_image}
										alt="bride"
										data-dispatch-id={fieldMap.BRIDE_IMAGE}
										className={`partner-img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
									/>
								</div>
							)}
							<p className="partner-name animate-on-scroll delay-2" data-dispatch-id={fieldMap.BRIDE_NAME}>
								{data?.bride_name || "Bride"}
							</p>
							<PartnersSocialList
								socials={data?.partner_socials_2 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_2}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Section 04 — Quran */}
			{data?.is_islamic && (
				<div className="section_04">
					<div className="quran-paper">
						<BotanicalCorner position="tl" />
						<BotanicalCorner position="tr" />
						<div className="quran-inner">
							<p className="quran-basmala">بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيم</p>
							<GoldDivider />
							<p className="quran-verse">
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُونَ ﴾
							</p>
							<GoldDivider />
							<p className="quran-sadaqa">صَدَقَ اللهُ العَظِيم</p>
						</div>
					</div>
				</div>
			)}

			{/* Section 05 — Love Story */}
			{!data?.fieldVisibility?.loveStory?.hidden && (
				<div className="section_05">
					<div className="section-inner">
						<p className="section-title">{lang.our_love_story}</p>
						<GoldDivider />
						{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
							<div className="story-image-wrap">
								<img
									src={data?.story_image}
									alt="story"
									data-dispatch-id={fieldMap.STORY_IMAGE}
									className={`story-image ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}
								/>
								<div className="story-img-overlay" />
							</div>
						)}
						{data?.story?.length > 0 && (
							<div className={`story-list ${data?.language === LANG_AR ? RTL : ""}`} data-dispatch-id={fieldMap.STORY}>
								{data?.story.map((story, index) => (
									<div key={story._id} className={`story-item animate-on-scroll delay-${index + 1}`}>
										<div className="story-dot" />
										<div className="story-content">
											<p className={`story-title ${data?.language === LANG_AR ? RTL : ""}`}>{story.title}</p>
											<p className={`story-description ${data?.language === LANG_AR ? RTL : ""}`}>
												{story.description}
											</p>
										</div>
									</div>
								))}
							</div>
						)}
						{canEdit && (data?.story || []).length === 0 && (
							<div>
								<div data-dispatch-id={fieldMap.STORY}>
									<div className="add_story">
										<AddIcon />
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Section 06 — Gallery */}
			{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
				<div className="section_06">
					<p className="section-title">{lang.our_gallery}</p>
					<GoldDivider />
					<div data-dispatch-id={fieldMap.GALLERY} className="gallery-wrap">
						<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
					</div>
				</div>
			)}

			{/* Section 07 — Comments */}
			{!data?.fieldVisibility?.comments?.hidden && (
				<div className="section_07" ref={commentsSectionRef as React.RefObject<HTMLDivElement>}>
					<div className="section-inner">
						<p className="section-title">{lang.comments || "Comments"}</p>
						<GoldDivider />
						{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
							<p
								className={`comments-text animate-on-scroll delay-1 ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.COMMENTS_TEXT}
							>
								{data?.comments_text}
							</p>
						)}
						{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
							<p
								className={`comment-sub-text ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
							>
								{data?.comments_sub_text}
							</p>
						)}
						<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrixProp} />
					</div>
				</div>
			)}

			{/* Section 08 — Location */}
			{!data?.fieldVisibility?.location?.hidden && (
				<div className="section_08" ref={locationSectionRef as React.RefObject<HTMLDivElement>}>
					<div className="section-inner">
						<p className="section-title">{invitation_location_header}</p>
						<GoldDivider />
						{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
							<p
								className={`location-text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.LOCATION_TEXT}
							>
								{data?.wedding_location_text}
							</p>
						)}
						<div className="map-wrap" data-dispatch-id={fieldMap.MAP}>
							<MapComponent canEdit={canEdit} />
						</div>
						<div className="map-btn-wrap" data-dispatch-id={fieldMap.MAP}>
							<MapButton canEdit={canEdit} text={lang.location} />
						</div>

						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<div
								className={`conclusion-img-wrap ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.MAIN_IMAGE}
							>
								<img src={data?.main_image} alt="couple" className="conclusion-img" />
								<div className="conclusion-img-overlay" />
							</div>
						)}

						<div className={`names-conclusion ${data?.language === LANG_AR ? RTL : ""} animate-on-scroll delay-1`}>
							<span data-dispatch-id={fieldMap.GROOM_NAME}>{data?.groom_name}</span>
							<span className="and-text">{lang.and}</span>
							<span data-dispatch-id={fieldMap.BRIDE_NAME}>{data?.bride_name}</span>
						</div>

						{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
							<div>
								<p
									className={`conclusion-text animate-on-scroll delay-2 ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""}`}
									data-dispatch-id={fieldMap.CONCLUSION_TEXT}
								>
									{data?.conclusion_text}
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			<Footer colormatrix={colorMatrixProp} />
		</TemplateWrapper>
	);
});

export default Template;
